import {
  IonButton,
  IonContent,
  IonModal,
  IonPage,
  IonSkeletonText,
  IonSpinner,
  IonTextarea,
  IonToast,
  useIonActionSheet,
  useIonAlert,
} from '@ionic/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Suspense, lazy, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useInvoiceQuery } from '~/api/hooks/useInvoices';
import {
  CANCEL_REASON_OPTIONS,
  type CancelReasonCode,
  type InvoiceDetail,
  type InvoiceStatus,
  cancelInvoice,
  downloadInvoiceDocument,
  retryInvoice,
} from '~/api/invoices';
import { queryKeys } from '~/api/queryKeys';
import { ErrorBoundary } from '~/components/ErrorBoundary';
import { GridPattern } from '~/components/ui/GridPattern';
import { Icon, type RxIconName } from '~/components/ui/Icon';
import { SectionHead } from '~/components/ui/SectionHead';
import { useAppNavigation } from '~/hooks/useAppNavigation';
import { DOWNLOAD_URL_LIFETIME_MS } from '~/lib/download';
import { formatDateTime, splitBrl } from '~/lib/format';
import { applyCpfCnpjMask, formatBrlNumber } from '~/lib/masks';
import { ROUTES } from '~/routes';
import { ACCESS_LEVEL, atLeast, companyLevel } from '~/session/access';
import { useAuthStore } from '~/stores/auth';

// pdf.js é pesado (~1,4 MB) e só é necessário ao abrir uma nota com PDF.
// Carregamos sob demanda pra não inflar o bundle inicial da PWA.
const PdfPreview = lazy(() =>
  import('~/components/ui/PdfPreview').then((module) => ({ default: module.PdfPreview })),
);

const STATUS_ICON: Record<InvoiceStatus, RxIconName> = {
  AUTHORIZED: 'check',
  PROCESSING: 'refresh',
  DRAFT: 'doc',
  ERROR: 'warn',
  REJECTED: 'warn',
  CANCELLED: 'lock',
};

const STATUS_PILL: Record<InvoiceStatus, string> = {
  AUTHORIZED: 'Autorizada',
  PROCESSING: 'Processando',
  DRAFT: 'Rascunho',
  ERROR: 'Erro',
  REJECTED: 'Rejeitada',
  CANCELLED: 'Cancelada',
};

export function NotaDetalhe(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const nav = useAppNavigation();
  const query = useInvoiceQuery(id);

  return (
    <IonPage>
      <IonContent fullscreen>
        <div className="rx-container rx-screen" style={{ paddingBottom: 28 }}>
          {query.isPending ? (
            <DetalheSkeleton onBack={() => nav.replace(ROUTES.notas)} />
          ) : query.isError ? (
            <ErrorState
              message={query.error.message}
              onRetry={() => query.refetch()}
              onBack={() => nav.replace(ROUTES.notas)}
            />
          ) : (
            <DetalheContent invoice={query.data} onBack={() => nav.replace(ROUTES.notas)} />
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}

interface DetalheContentProps {
  invoice: InvoiceDetail;
  onBack: () => void;
}

function DetalheContent({ invoice, onBack }: DetalheContentProps): JSX.Element {
  const queryClient = useQueryClient();
  const [presentAlert] = useIonAlert();
  const [presentActionSheet] = useIonActionSheet();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const blobRef = useRef<{ blob: Blob; url: string } | null>(null);
  // O nível vem da empresa emissora da nota, não da selecionada: a nota pode ter sido
  // aberta por link enquanto outra empresa está em operação.
  const companies = useAuthStore((state) => state.companies);
  const issuer = companies.find((company) => company.id === invoice.client);
  const canWrite = atLeast(companyLevel(issuer, 'nfse'), ACCESS_LEVEL.full);

  const canDownloadPdf = invoice.status === 'AUTHORIZED' || invoice.status === 'CANCELLED';
  const canCancel = canWrite && invoice.status === 'AUTHORIZED';
  // Só ERROR é reemitível: REJECTED é recusa de negócio da prefeitura e reenviar
  // o mesmo documento seria recusado de novo. O backend aplica a mesma regra.
  const canRetry = canWrite && invoice.status === 'ERROR';
  // DPS enviada sem resposta da Sefin: a nota pode já existir na prefeitura, então a
  // retentativa começa consultando o destino dela em vez de emitir de novo.
  const awaitingSefin = invoice.awaiting_sefin_confirmation;
  // O PDF sai por URL assinada pedida na hora; `pdf_available` fica false enquanto a
  // DANFSe ainda não foi gerada, mesmo com a nota já autorizada. `updated_at` entra
  // na chave porque o mesmo objeto é regravado quando a nota muda (cancelamento
  // estampa a chancela) e o PDF precisa ser baixado de novo.
  const pdfVersion = canDownloadPdf && invoice.pdf_available ? invoice.updated_at : null;
  const invoiceId = invoice.id;

  useEffect(() => {
    if (!pdfVersion) {
      if (blobRef.current) URL.revokeObjectURL(blobRef.current.url);
      blobRef.current = null;
      setPdfUrl(null);
      setPdfError(null);
      return;
    }
    let cancelled = false;
    setLoadingPdf(true);
    setPdfError(null);
    downloadInvoiceDocument(invoiceId, 'pdf')
      .then((blob) => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        if (blobRef.current) URL.revokeObjectURL(blobRef.current.url);
        blobRef.current = { blob, url };
        setPdfUrl(url);
      })
      .catch((error: Error) => {
        if (cancelled) return;
        setPdfError(error.message || 'Não foi possível carregar o PDF.');
      })
      .finally(() => {
        if (!cancelled) setLoadingPdf(false);
      });
    return () => {
      cancelled = true;
    };
  }, [invoiceId, pdfVersion]);

  useEffect(() => {
    return () => {
      if (blobRef.current) URL.revokeObjectURL(blobRef.current.url);
    };
  }, []);

  const cancelMutation = useMutation({
    mutationFn: ({ codigo, reason }: { codigo: CancelReasonCode; reason: string }) =>
      cancelInvoice(invoice.id, codigo, reason),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
      setCancelOpen(false);
      setToast('Solicitação de cancelamento enviada.');
    },
    onError: (error: Error) => {
      setToast(`Erro: ${error.message}`);
    },
  });

  const retryMutation = useMutation({
    mutationFn: () => retryInvoice(invoice.id),
    onSuccess: async () => {
      // Volta pra PROCESSING; o polling do useInvoiceQuery acompanha até o status final.
      await queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
      setToast('Nota reenviada para emissão.');
    },
    onError: (error: Error) => {
      setToast(`Erro ao reemitir: ${error.message}`);
    },
  });

  const handleDownload = (): void => {
    if (!blobRef.current) return;
    const filename = invoice.pdf_filename;
    const anchor = document.createElement('a');
    anchor.href = blobRef.current.url;
    anchor.download = filename;
    anchor.click();
  };

  const handleShare = async (): Promise<void> => {
    if (!blobRef.current) return;
    const filename = invoice.pdf_filename;
    const file = new File([blobRef.current.blob], filename, { type: 'application/pdf' });
    const data: ShareData = {
      title: `NFSe ${invoice.numero ?? invoice.id_integracao}`,
      text: `${invoice.tomador_razao_social} · R$ ${formatBrlNumber(Number(invoice.valor_servico))}`,
      files: [file],
    };
    const canShareFiles =
      typeof navigator.canShare === 'function' && navigator.canShare({ files: [file] });
    try {
      if (canShareFiles && navigator.share) {
        await navigator.share(data);
        return;
      }
      if (navigator.share) {
        await navigator.share({ title: data.title, text: data.text });
        return;
      }
      handleDownload();
    } catch (error) {
      if ((error as DOMException).name !== 'AbortError') {
        handleDownload();
      }
    }
  };

  const handleDownloadXml = async (): Promise<void> => {
    if (!invoice.xml_available) {
      setToast('XML ainda não disponível para esta nota.');
      return;
    }
    try {
      const blob = await downloadInvoiceDocument(invoice.id, 'xml');
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = invoice.xml_filename;
      anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(url), DOWNLOAD_URL_LIFETIME_MS);
    } catch (error) {
      setToast(`Erro ao baixar XML: ${(error as Error).message}`);
    }
  };

  const openMoreMenu = (): void => {
    presentActionSheet({
      header: 'Mais ações',
      buttons: [
        {
          text: 'Baixar XML',
          handler: () => {
            void handleDownloadXml();
          },
        },
        { text: 'Fechar', role: 'cancel' },
      ],
    });
  };

  const openCancelConfirm = (): void => {
    if (!canCancel) return;
    presentAlert({
      header: 'Cancelar nota?',
      message: 'O cancelamento é enviado à prefeitura e não pode ser desfeito.',
      buttons: [
        { text: 'Voltar', role: 'cancel' },
        {
          text: 'Continuar',
          role: 'destructive',
          handler: () => setCancelOpen(true),
        },
      ],
    });
  };

  const valorParts = splitBrl(invoice.valor_servico);

  return (
    <>
      <DetalheHero
        invoice={invoice}
        valorParts={valorParts}
        onBack={onBack}
        onMore={canDownloadPdf ? openMoreMenu : undefined}
      />

      {invoice.error_message && (
        <div style={{ padding: '16px 24px 0' }}>
          <div
            style={{
              padding: '12px 14px',
              background: 'var(--rx-negative-tint)',
              border: '0.5px solid var(--rx-negative)',
              borderRadius: 12,
              display: 'flex',
              gap: 10,
            }}
          >
            <Icon name="warn" size={16} color="var(--rx-negative)" />
            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: '0.4px',
                  textTransform: 'uppercase',
                  color: 'var(--rx-negative)',
                }}
              >
                {/* ERROR é falha de sistema (rede/certificado); só REJECTED vem da prefeitura. */}
                {awaitingSefin
                  ? 'Aguardando confirmação da Sefin'
                  : canRetry
                    ? 'Falha no envio'
                    : 'Mensagem da prefeitura'}
              </div>
              <p
                style={{
                  marginTop: 4,
                  fontSize: 13,
                  color: 'var(--rx-text)',
                  whiteSpace: 'pre-line',
                }}
              >
                {invoice.error_message}
              </p>
              {canRetry && (
                <IonButton
                  className="rx-button"
                  style={{ marginTop: 12, width: '100%' }}
                  disabled={retryMutation.isPending}
                  onClick={() => retryMutation.mutate()}
                >
                  {retryMutation.isPending ? (
                    <IonSpinner name="dots" style={{ width: 20, height: 20 }} />
                  ) : (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <Icon name="refresh" size={16} color="var(--rx-brand-ink)" />
                      {awaitingSefin ? 'Consultar Sefin' : 'Reemitir nota'}
                    </span>
                  )}
                </IonButton>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Detail rows */}
      <div style={{ padding: '16px 24px 0' }}>
        <DetailList
          rows={[
            { label: 'Tomador', value: invoice.tomador_razao_social },
            {
              label: 'CPF/CNPJ',
              value: applyCpfCnpjMask(invoice.tomador_cpf_cnpj),
              mono: true,
            },
            invoice.numero != null
              ? { label: 'Número', value: `Nº ${invoice.numero}`, mono: true }
              : null,
            invoice.serie ? { label: 'Série', value: invoice.serie, mono: true } : null,
            invoice.codigo_verificacao
              ? { label: 'Cód. verificação', value: invoice.codigo_verificacao, mono: true }
              : null,
          ]}
        />
      </div>

      {/* Tributação breakdown */}
      <SectionHead label="Valores e tributação" />
      <div style={{ padding: '0 24px' }}>
        <div
          style={{
            background: 'var(--rx-surface)',
            border: '0.5px solid var(--rx-border)',
            borderRadius: 14,
            padding: '14px 16px',
          }}
        >
          <BreakdownRow
            label="Valor do serviço"
            value={`R$ ${formatBrlNumber(Number(invoice.valor_servico))}`}
            first
          />
          <BreakdownRow label="ISS" value={`R$ ${formatBrlNumber(Number(invoice.valor_iss))}`} />
          {Number(invoice.valor_retencoes) > 0 && (
            <BreakdownRow
              label="Retenções federais"
              value={`− R$ ${formatBrlNumber(Number(invoice.valor_retencoes))}`}
              muted
            />
          )}
          <div
            style={{
              marginTop: 10,
              paddingTop: 12,
              borderTop: '0.5px solid var(--rx-border-strong)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <span
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--rx-text)',
                letterSpacing: '0.3px',
              }}
            >
              Valor líquido
            </span>
            <span
              className="rx-mono"
              style={{ fontSize: 16, fontWeight: 600, color: 'var(--rx-brand)' }}
            >
              R$ {formatBrlNumber(Number(invoice.valor_liquido))}
            </span>
          </div>
        </div>
      </div>

      {/* Serviço */}
      {invoice.items[0] && (
        <>
          <SectionHead label="Serviço prestado" />
          <div style={{ padding: '0 24px' }}>
            <div
              style={{
                background: 'var(--rx-surface)',
                border: '0.5px solid var(--rx-border)',
                borderRadius: 14,
                padding: '14px 16px',
              }}
            >
              <div
                className="rx-mono"
                style={{ fontSize: 11, color: 'var(--rx-brand)', fontWeight: 600 }}
              >
                {invoice.items[0].codigo_servico || invoice.items[0].codigo_servico_nacional}
              </div>
              <p
                style={{
                  marginTop: 6,
                  fontSize: 13,
                  color: 'var(--rx-text)',
                  lineHeight: 1.5,
                  whiteSpace: 'pre-line',
                }}
              >
                {invoice.items[0].discriminacao}
              </p>
            </div>
          </div>
        </>
      )}

      {/* Trilha — datas */}
      <SectionHead label="Trilha da nota" />
      <div style={{ padding: '0 24px' }}>
        <Timeline
          entries={[
            { what: 'Criada no app', when: formatDateTime(invoice.created_at) },
            invoice.data_emissao
              ? { what: 'Emissão enviada', when: formatDateTime(invoice.data_emissao) }
              : null,
            invoice.data_autorizacao
              ? {
                  what: 'Autorizada pela prefeitura',
                  when: formatDateTime(invoice.data_autorizacao),
                }
              : null,
            invoice.cancelled_at
              ? {
                  what: 'Cancelada pela prefeitura',
                  when: formatDateTime(invoice.cancelled_at),
                }
              : null,
          ]}
        />
      </div>

      {/* Documento + ações */}
      {canDownloadPdf && (
        <>
          <SectionHead
            label="Documento"
            action={
              pdfUrl ? (
                <a
                  href={pdfUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  style={{ color: 'inherit' }}
                >
                  Abrir
                </a>
              ) : null
            }
          />
          <div style={{ padding: '0 24px' }}>
            <div
              style={{
                background: 'var(--rx-surface)',
                border: '0.5px solid var(--rx-border)',
                borderRadius: 14,
                overflow: 'hidden',
              }}
            >
              {!invoice.pdf_available ? (
                <div
                  style={{
                    padding: '18px 16px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                  }}
                >
                  <Icon name="doc" size={16} color="var(--rx-text-3)" />
                  <p style={{ fontSize: 13, color: 'var(--rx-text-2)' }}>
                    O PDF da nota ainda está sendo gerado. Volte em instantes.
                  </p>
                </div>
              ) : (
                <>
                  {loadingPdf ? (
                    <div style={{ padding: 36, display: 'flex', justifyContent: 'center' }}>
                      <IonSpinner name="dots" />
                    </div>
                  ) : pdfError ? (
                    <PdfWarning message={pdfError} />
                  ) : pdfUrl ? (
                    <ErrorBoundary key={pdfUrl} fallback={renderPdfPreviewFailure}>
                      <Suspense
                        fallback={
                          <div style={{ padding: 36, display: 'flex', justifyContent: 'center' }}>
                            <IonSpinner name="dots" />
                          </div>
                        }
                      >
                        <PdfPreview
                          fileUrl={pdfUrl}
                          label={`PDF da NFSe ${invoice.numero ?? invoice.id_integracao}`}
                        />
                      </Suspense>
                    </ErrorBoundary>
                  ) : null}

                  <div
                    style={{
                      display: 'flex',
                      gap: 8,
                      padding: '12px 14px',
                      borderTop: '0.5px solid var(--rx-border)',
                    }}
                  >
                    <button
                      type="button"
                      onClick={handleShare}
                      disabled={!pdfUrl}
                      style={{
                        flex: 1,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                        padding: '10px 12px',
                        borderRadius: 10,
                        background: 'var(--rx-surface)',
                        border: '0.5px solid var(--rx-border)',
                        color: 'var(--rx-text)',
                        fontSize: 13,
                        fontWeight: 500,
                        opacity: pdfUrl ? 1 : 0.5,
                        cursor: pdfUrl ? 'pointer' : 'not-allowed',
                      }}
                    >
                      <Icon name="paperclip" size={14} />
                      Compartilhar
                    </button>
                    <button
                      type="button"
                      onClick={handleDownload}
                      disabled={!pdfUrl}
                      style={{
                        flex: 1,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                        padding: '10px 12px',
                        borderRadius: 10,
                        background: 'var(--rx-brand)',
                        color: 'var(--rx-brand-ink)',
                        border: 'none',
                        fontSize: 13,
                        fontWeight: 500,
                        opacity: pdfUrl ? 1 : 0.5,
                        cursor: pdfUrl ? 'pointer' : 'not-allowed',
                      }}
                    >
                      <Icon name="download" size={14} />
                      Baixar PDF
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </>
      )}

      {canCancel && (
        <div style={{ padding: '20px 24px 0' }}>
          <button
            type="button"
            onClick={openCancelConfirm}
            style={{
              width: '100%',
              padding: '14px 16px',
              background: 'var(--rx-surface)',
              border: '0.5px solid var(--rx-negative)',
              borderRadius: 14,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 9,
                background: 'var(--rx-negative-tint)',
                color: 'var(--rx-negative)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Icon name="warn" size={18} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--rx-negative)' }}>
                Cancelar nota fiscal
              </div>
              <div style={{ marginTop: 2, fontSize: 12, color: 'var(--rx-text-2)' }}>
                Motivo obrigatório, mínimo 15 caracteres.
              </div>
            </div>
            <Icon name="chevron" size={16} color="var(--rx-negative)" />
          </button>
        </div>
      )}

      <CancelModal
        isOpen={cancelOpen}
        onClose={() => setCancelOpen(false)}
        onConfirm={(codigo, reason) => cancelMutation.mutate({ codigo, reason })}
        isSubmitting={cancelMutation.isPending}
      />

      <IonToast
        isOpen={Boolean(toast)}
        message={toast ?? ''}
        duration={3000}
        onDidDismiss={() => setToast(null)}
      />
    </>
  );
}

const PDF_PREVIEW_FAILURE =
  'Não foi possível mostrar a prévia do PDF neste aparelho. Use "Baixar PDF" ou "Compartilhar".';

// A prévia tem a própria boundary: uma falha do pdf.js (navegador antigo, PDF corrompido)
// fica no cartão do PDF, e a nota, o download e o compartilhamento continuam na tela.
function renderPdfPreviewFailure(): JSX.Element {
  return <PdfWarning message={PDF_PREVIEW_FAILURE} />;
}

function PdfWarning({ message }: { message: string }): JSX.Element {
  return (
    <div
      role="alert"
      style={{
        padding: '14px 16px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
      }}
    >
      <Icon name="warn" size={16} color="var(--rx-negative)" />
      <p style={{ fontSize: 13, color: 'var(--rx-text)' }}>{message}</p>
    </div>
  );
}

interface DetalheHeroProps {
  invoice: InvoiceDetail;
  valorParts: { integer: string; cents: string };
  onBack: () => void;
  onMore?: () => void;
}

function DetalheHero({ invoice, valorParts, onBack, onMore }: DetalheHeroProps): JSX.Element {
  return (
    <div
      style={{
        background: 'var(--rx-brand)',
        color: '#fff',
        paddingBottom: 28,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <GridPattern
        size={200}
        step={10}
        stroke="#fff"
        strokeWidth={0.6}
        opacity={0.06}
        vertical={false}
        style={{ position: 'absolute', right: -40, top: 0 }}
      />

      <div
        style={{
          position: 'relative',
          zIndex: 1,
          padding: '16px 24px 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <button
          type="button"
          onClick={onBack}
          aria-label="Voltar"
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            border: '0.5px solid rgba(255,255,255,0.2)',
            background: 'rgba(255,255,255,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            cursor: 'pointer',
          }}
        >
          <Icon name="back" size={18} />
        </button>
        {onMore ? (
          <button
            type="button"
            aria-label="Mais"
            onClick={onMore}
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              border: '0.5px solid rgba(255,255,255,0.2)',
              background: 'rgba(255,255,255,0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              cursor: 'pointer',
            }}
          >
            <Icon name="more" size={18} />
          </button>
        ) : (
          <div style={{ width: 36, height: 36 }} aria-hidden />
        )}
      </div>

      <div style={{ padding: '22px 24px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              padding: '3px 8px',
              borderRadius: 4,
              background: 'rgba(255,255,255,0.15)',
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.5px',
              textTransform: 'uppercase',
            }}
          >
            <Icon name={STATUS_ICON[invoice.status]} size={11} />
            {STATUS_PILL[invoice.status]}
          </span>
          <span className="rx-mono" style={{ fontSize: 11, opacity: 0.8 }}>
            {invoice.numero != null ? `Nº ${invoice.numero}` : invoice.id_integracao}
          </span>
        </div>
        <div
          style={{
            marginTop: 12,
            display: 'flex',
            alignItems: 'baseline',
            gap: 6,
          }}
        >
          <span style={{ fontSize: 14, opacity: 0.6 }}>R$</span>
          <span
            className="rx-mono"
            style={{ fontSize: 36, fontWeight: 500, letterSpacing: '-1px' }}
          >
            {valorParts.integer}
          </span>
          <span style={{ fontSize: 18, opacity: 0.6 }}>,{valorParts.cents}</span>
        </div>
        <div
          style={{
            marginTop: 6,
            fontSize: 14,
            opacity: 0.85,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {invoice.tomador_razao_social}
        </div>
      </div>
    </div>
  );
}

type DetailRow = { label: string; value: string; mono?: boolean } | null;

function DetailList({ rows }: { rows: DetailRow[] }): JSX.Element {
  const filtered = rows.filter(
    (r): r is { label: string; value: string; mono?: boolean } => r !== null,
  );
  return (
    <div
      style={{
        background: 'var(--rx-surface)',
        border: '0.5px solid var(--rx-border)',
        borderRadius: 14,
        overflow: 'hidden',
      }}
    >
      {filtered.map((row, idx) => (
        <div
          key={row.label}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '13px 16px',
            gap: 12,
            borderTop: idx === 0 ? 'none' : '0.5px solid var(--rx-border)',
          }}
        >
          <span style={{ fontSize: 12, color: 'var(--rx-text-3)', fontWeight: 500 }}>
            {row.label}
          </span>
          <span
            className={row.mono ? 'rx-mono' : ''}
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: 'var(--rx-text)',
              textAlign: 'right',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {row.value}
          </span>
        </div>
      ))}
    </div>
  );
}

interface BreakdownRowProps {
  label: string;
  value: string;
  first?: boolean;
  muted?: boolean;
}

function BreakdownRow({ label, value, first, muted }: BreakdownRowProps): JSX.Element {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '7px 0',
        borderTop: first ? 'none' : '0.5px solid var(--rx-border)',
      }}
    >
      <span
        style={{
          fontSize: 12,
          fontWeight: 500,
          color: muted ? 'var(--rx-text-2)' : 'var(--rx-text)',
        }}
      >
        {label}
      </span>
      <span
        className="rx-mono"
        style={{
          fontSize: 12,
          fontWeight: 500,
          color: muted ? 'var(--rx-text-2)' : 'var(--rx-text)',
        }}
      >
        {value}
      </span>
    </div>
  );
}

type TimelineEntry = { what: string; when: string } | null;

function Timeline({ entries }: { entries: TimelineEntry[] }): JSX.Element {
  const filtered = entries.filter((e): e is { what: string; when: string } => e !== null);
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {filtered.map((entry, idx) => (
        <div
          key={`${entry.what}-${idx}`}
          style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              paddingTop: 4,
            }}
          >
            <div
              style={{
                width: 7,
                height: 7,
                borderRadius: 7,
                background: 'var(--rx-brand)',
              }}
            />
            {idx < filtered.length - 1 && (
              <div
                style={{
                  width: 1,
                  flex: 1,
                  background: 'var(--rx-border-strong)',
                  marginTop: 2,
                  minHeight: 22,
                }}
              />
            )}
          </div>
          <div style={{ flex: 1, paddingBottom: 14 }}>
            <div style={{ fontSize: 13, color: 'var(--rx-text)' }}>{entry.what}</div>
            <div
              className="rx-mono"
              style={{ marginTop: 2, fontSize: 10, color: 'var(--rx-text-3)' }}
            >
              {entry.when}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

interface CancelModalProps {
  isOpen: boolean;
  onClose(): void;
  onConfirm(codigo: CancelReasonCode, reason: string): void;
  isSubmitting: boolean;
}

const CANCEL_MIN_LENGTH = 15;

function CancelModal({ isOpen, onClose, onConfirm, isSubmitting }: CancelModalProps): JSX.Element {
  const [codigo, setCodigo] = useState<CancelReasonCode | null>(null);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setCodigo(null);
      setReason('');
    }
  }, [isOpen]);

  const trimmed = reason.trim();
  const isValid = codigo !== null && trimmed.length >= CANCEL_MIN_LENGTH;

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose}>
      <div
        className="rx-container rx-screen"
        style={{
          padding: '24px 24px 24px',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingBottom: 16,
            borderBottom: '0.5px solid var(--rx-border)',
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: 22,
              fontWeight: 500,
              color: 'var(--rx-text)',
              letterSpacing: '-0.4px',
            }}
          >
            Cancelar NFSe
          </h2>
          <button type="button" onClick={onClose} aria-label="Fechar" className="rx-iconbtn">
            <Icon name="back" size={18} />
          </button>
        </header>

        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <p style={{ marginTop: 18, fontSize: 14, color: 'var(--rx-text-2)' }}>
            Escolha o motivo e descreva o cancelamento. Aparece no histórico da prefeitura.
          </p>

          <div style={{ marginTop: 18 }}>
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.6px',
                textTransform: 'uppercase',
                color: 'var(--rx-text-3)',
              }}
            >
              Motivo
            </span>
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {CANCEL_REASON_OPTIONS.map((option) => {
                const selected = codigo === option.codigo;
                return (
                  <button
                    key={option.codigo}
                    type="button"
                    onClick={() => setCodigo(option.codigo)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '13px 14px',
                      borderRadius: 12,
                      border: selected
                        ? '1px solid var(--rx-brand)'
                        : '0.5px solid var(--rx-border)',
                      background: 'var(--rx-surface)',
                      color: 'var(--rx-text)',
                      fontSize: 14,
                      fontWeight: 500,
                      textAlign: 'left',
                      cursor: 'pointer',
                    }}
                  >
                    <span
                      aria-hidden
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: 9,
                        flexShrink: 0,
                        border: selected
                          ? '5px solid var(--rx-brand)'
                          : '1.5px solid var(--rx-border-strong)',
                      }}
                    />
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="rx-field" style={{ marginTop: 18 }}>
            <label
              htmlFor="cancel-reason"
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.6px',
                textTransform: 'uppercase',
                color: 'var(--rx-text-3)',
              }}
            >
              Descrição do motivo
            </label>
            <IonTextarea
              id="cancel-reason"
              value={reason}
              onIonInput={(event) => setReason(event.detail.value ?? '')}
              placeholder="Ex.: Emitida em duplicidade para o mesmo serviço."
              rows={5}
              maxlength={500}
            />
            <p
              style={{
                marginTop: 6,
                fontSize: 12,
                color: isValid ? 'var(--rx-text-3)' : 'var(--rx-negative)',
              }}
            >
              {trimmed.length}/{CANCEL_MIN_LENGTH} mínimo
            </p>
          </div>
        </div>

        <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <IonButton
            disabled={!isValid || isSubmitting}
            onClick={() => codigo && onConfirm(codigo, trimmed)}
            className="rx-button"
            expand="block"
          >
            {isSubmitting ? (
              <IonSpinner name="dots" />
            ) : (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <Icon name="warn" size={16} color="var(--rx-brand-ink)" />
                Cancelar nota fiscal
              </span>
            )}
          </IonButton>
          <button
            type="button"
            onClick={onClose}
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: 'var(--rx-text-2)',
              padding: 12,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            Voltar
          </button>
        </div>
      </div>
    </IonModal>
  );
}

interface SkeletonProps {
  onBack: () => void;
}

function DetalheSkeleton({ onBack }: SkeletonProps): JSX.Element {
  return (
    <>
      <div
        style={{
          background: 'var(--rx-brand)',
          paddingBottom: 32,
          padding: '16px 24px 32px',
        }}
      >
        <button
          type="button"
          onClick={onBack}
          aria-label="Voltar"
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            border: '0.5px solid rgba(255,255,255,0.2)',
            background: 'rgba(255,255,255,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            cursor: 'pointer',
          }}
        >
          <Icon name="back" size={18} />
        </button>
        <IonSkeletonText animated style={{ width: 90, height: 14, marginTop: 22 }} />
        <IonSkeletonText animated style={{ width: 180, height: 36, marginTop: 14 }} />
        <IonSkeletonText animated style={{ width: 220, height: 14, marginTop: 12 }} />
      </div>
      <div style={{ padding: '16px 24px 0' }}>
        <div
          style={{
            background: 'var(--rx-surface)',
            border: '0.5px solid var(--rx-border)',
            borderRadius: 14,
            padding: '14px 16px',
          }}
        >
          <IonSkeletonText animated style={{ width: '60%', height: 12 }} />
          <IonSkeletonText animated style={{ width: '40%', height: 12, marginTop: 10 }} />
        </div>
      </div>
    </>
  );
}

interface ErrorStateProps {
  message: string;
  onRetry: () => void;
  onBack: () => void;
}

function ErrorState({ message, onRetry, onBack }: ErrorStateProps): JSX.Element {
  return (
    <div style={{ padding: '20px 24px 0' }}>
      <button type="button" onClick={onBack} className="rx-iconbtn" aria-label="Voltar">
        <Icon name="back" size={18} />
      </button>
      <div style={{ marginTop: 36, textAlign: 'center' }}>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 52,
            height: 52,
            borderRadius: 26,
            background: 'var(--rx-negative-tint)',
            color: 'var(--rx-negative)',
          }}
        >
          <Icon name="warn" size={22} />
        </div>
        <p
          style={{
            marginTop: 14,
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.6px',
            textTransform: 'uppercase',
            color: 'var(--rx-negative)',
          }}
        >
          Falha ao carregar
        </p>
        <h2
          style={{
            marginTop: 8,
            fontSize: 22,
            fontWeight: 500,
            color: 'var(--rx-text)',
            letterSpacing: '-0.4px',
          }}
        >
          Não conseguimos buscar a nota.
        </h2>
        <p
          style={{
            marginTop: 8,
            fontSize: 13,
            color: 'var(--rx-text-2)',
            maxWidth: '34ch',
            marginInline: 'auto',
          }}
        >
          {message}
        </p>
        <IonButton onClick={onRetry} className="rx-button" style={{ marginTop: 18, width: '100%' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Icon name="refresh" size={16} color="var(--rx-brand-ink)" />
            Tentar novamente
          </span>
        </IonButton>
      </div>
    </div>
  );
}
